import * as fs from 'fs';
import * as os from 'os';
import * as path from 'path';
import * as utils from '../../../utils';
import * as npmWrapper from '../../../commons/npmWrapper';
import dataUriToBuffer from 'data-uri-to-buffer';
import { Worker } from 'worker_threads';
import { TimeoutError } from '../../../errors';
import { getLogger } from '../../../commons/logger';
import { getS3Artifact } from '../../../commons/testimServicesApi';
import { getLocallyInstalledCommonJsPath } from '../../../commons/npmWrapper';
import { featureFlagsService } from '../../../commons/featureFlagsService';
import type { IncomingParams } from 'clickim/src/background/playbackContext';

const logger = getLogger('cli-service');

function convertWindowsBackslash(input: string) {
    const isExtendedLengthPath = input.startsWith('\\\\?\\');
    const hasNonAscii = /[^\u0000-\u0080]+/.test(input); // eslint-disable-line no-control-regex

    if (isExtendedLengthPath || hasNonAscii) {
        return input;
    }

    return input.replace(/\\/g, '/');
}

function requireOrImportMethod(_path: string) {
    try {
        return { sync: true, lib: globalThis.require(_path) };
    } catch (err) {
        if (err.code !== 'ERR_REQUIRE_ESM') {
            throw err;
        }
        const { sep, join } = globalThis.require('path') as typeof import('path');
        const { promises } = globalThis.require('fs') as typeof import('fs');

        const lib = promises.readFile(`${_path}${sep}package.json`).then(file => {
            // @ts-expect-error JSON.parse handles buffers as well, the types are not smart enough :(
            const packageJson = JSON.parse(file);
            const fullPath = join(_path, packageJson.main || `${sep}index.js`);
            return import(fullPath);
        });

        return { sync: false, lib };
    }
}

function constructWithArguments(params: string[]) {
    const AsyncFunction = Object.getPrototypeOf(async () => null).constructor;
    function F() {
        // eslint-disable-next-line prefer-spread
        return AsyncFunction.apply(null, params);
    }

    F.prototype = AsyncFunction.prototype;
    // @ts-expect-error dark magic
    return new F();
}

export interface ConsoleItem {
    method: 'log' | 'info' | 'warn' | 'error' | 'debug';
    msg: string;
    timestamp: number;
}
interface RunCodeRes {
    tstConsoleLogs: ConsoleItem[];
    status: 'done' | 'failed';
    result: { resultValue: any; exports: object; exportsTest: object; exportsGlobal: object };
    success: boolean;
}

async function runCodeWithWorkerThread(
    transactionId: string,
    incomingParams: Pick<IncomingParams, 'as'>,
    context: object,
    code: string,
    packageLocalLocations: { [pkg: string]: string } = {},
    timeout: number = undefined!,
    fileDataUrl: string | undefined = undefined,
): Promise<RunCodeRes> {
    const requireCode = Object.keys(packageLocalLocations).map(pMember => `
        var res = requireOrImportMethod('${convertWindowsBackslash(packageLocalLocations[pMember])}');
        if (res.sync) {
            var ${pMember} = res.lib;
        } else {
            var ${pMember} = await res.lib;
        }
        `).join('');

    const fileDataUrlToFileBuffer = !fileDataUrl ? 'var fileBuffer = null;' :
        `
            ${dataUriToBuffer.toString()}
            var fileBuffer = dataUriToBuffer('${fileDataUrl}');
        `;

    //https://github.com/anseki/console-substitute
    const consoleOverride = `
        const getMessage = arguments => {
            const args = Array.prototype.slice.call(arguments);
            let message = args.shift() + '';
            if (!args.length) {
                return message;
            }
            message = message.replace(/%([odifs])/g, function (s, param) {
                // Formatting is not yet supported.
                var arg;
                if (!args.length) {
                    return '';
                }
                arg = args.shift();
                if (param === 'o') {
                    return arg + '';
                } else if (param === 'd' || param === 'i') {
                    arg = typeof arg === 'boolean' ? (arg ? 1 : 0) : parseInt(arg, 10);
                    return isNaN(arg) ? '0' : arg + '';
                } else if (param === 'f') {
                    arg = typeof arg === 'boolean' ? (arg ? 1 : 0) : parseFloat(arg);
                    return isNaN(arg) ? '0.000000' : arg.toFixed(6) + '';
                } else if (param === 's') {
                    return arg + '';
                }
            });
            if (message) {
                args.unshift(message);
            }
            return args.join(' ').replace(/\\s*$/, ' ');
        };

        const pushNewMessage = (method, consoleArgs) => {
            const message = getMessage(consoleArgs);
            parentPort.postMessage({
                action: 'progress',
                data: {
                    method,
                    msg: message,
                    timestamp: Date.now(),
                }
            });
        };

        ["log", "info", "warn", "error", "debug"].forEach(function (method) {
            const nativeMethod = console[method];
            const oldMethod = nativeMethod?.bind(console);
            console[method] = function () {
                pushNewMessage(method, arguments);
                oldMethod?.apply(console, arguments);
            };
        });
    `;

    const injectCode = `
        function injectCode(params, args, incomingParams, context, code) {
            const constructWithArguments = ${constructWithArguments.toString()}

            const resolve = result => {
                parentPort.postMessage({
                    action: 'finish',
                    data: {
                        status: 'done',
                        result: result,
                        success: true,
                    }
                });
            };
            const reject = result => {
                parentPort.postMessage({
                    action: 'finish',
                    data: {
                        status: 'failed',
                        result: result,
                        success: false,
                    }
                });
            };

            try {
                params.push(code);
                const functionToRun = constructWithArguments(params);

                const result = functionToRun.apply(null, args);
                if (isPromise(result)) {
                    result.then(res => {
                        resolve({
                            resultValue: res,
                            exports: exportedData,
                            exportsTest: exportedTestData,
                            exportsGlobal: exportedGlobalData
                        });
                    }).catch(err => {
                        reject({
                            resultValue: err?.toString(),
                            exports: exportedData,
                            exportsTest: exportedTestData,
                            exportsGlobal: exportedGlobalData
                        });
                    });
                } else {
                    resolve({
                        resultValue: result,
                        exports: exportedData,
                        exportsTest: exportedTestData,
                        exportsGlobal: exportedGlobalData
                    });
                }
            } catch (e) {
                reject({
                    resultValue: e?.toString(),
                    exports: exportedData,
                    exportsTest: exportedTestData,
                    exportsGlobal: exportedGlobalData
                });
            }
        }
    `;

    const runFn = `
        (async function() {
            // A hack to fix an issue with stringified functions which use require. when compiling to JS, calls to require change to __require.
            const __require = require;
            const { parentPort } = require('worker_threads');
            const requireOrImportMethod = ${requireOrImportMethod.toString()}

            // requireCode will set async to be true if needed.
            ${requireCode}

            ${fileDataUrlToFileBuffer}

            ${consoleOverride}

            const isPromise = ${utils.isPromise.toString()}

            parentPort.once('message', input => {
                const { incomingParams, context, code } = input;

                const exportedData = {};
                const exportedTestData = {};
                const exportedGlobalData = {};

                const params = ["context"]
                    .concat(incomingParams.as.functionParameters)
                    .concat(${JSON.stringify(Object.keys(packageLocalLocations))})
                    .concat(['exports', 'exportsTest', 'exportsGlobal']);

                const args = [context]
                    .concat(incomingParams.as.functionArguments)
                    .concat([${Object.keys(packageLocalLocations).join(',')}])
                    .concat([exportedData, exportedTestData, exportedGlobalData]);

                if(fileBuffer) {
                    params.push('fileBuffer');
                    args.push(fileBuffer);
                }

                ${injectCode}

                injectCode(params, args, incomingParams, context, code);
            });
        })();
    `;

    const testimConsoleLogDataAggregates: ConsoleItem[] = [];
    const thread = new Worker(runFn, {
        eval: true,
    });
    try {
        return await utils.promiseTimeout(new Promise<RunCodeRes>((resolve) => {
            thread
                .on('message', message => {
                    if (message.action === 'finish') {
                        const { data } = message;
                        const messageWithLogs = { ...data, tstConsoleLogs: testimConsoleLogDataAggregates };
                        logger.debug('Run code worker response', { messageWithLogs, transactionId });
                        resolve(messageWithLogs);
                    } else if (message.action === 'progress') {
                        testimConsoleLogDataAggregates.push(message.data);
                    }
                })
                .on('error', err => {
                    if (err.message === 'malformed data: URI') {
                        logger.error('Run code worker error', { err, transactionId, fileDataUrl });
                    } else {
                        logger.error('Run code worker error', { err, transactionId });
                    }

                    resolve({
                        tstConsoleLogs: testimConsoleLogDataAggregates,
                        status: 'failed',
                        result: {
                            resultValue: err?.toString(),
                            exports: {},
                            exportsTest: {},
                            exportsGlobal: {},
                        },
                        success: false,
                    });
                })
                .on('exit', () => {
                    logger.debug('Run code worker has been terminated', { transactionId });
                });
            // context can contain methods and proxies which cannot pass.
            thread.postMessage({ incomingParams, context: JSON.parse(JSON.stringify(context)), code });
        }), timeout);
    } catch (err) {
        if (!(err instanceof TimeoutError)) {
            throw err;
        }
        logger.warn('timeout to run code', { transactionId, err });
        return {
            tstConsoleLogs: testimConsoleLogDataAggregates,
            status: 'failed',
            result: {
                // eslint-disable-next-line @typescript-eslint/no-base-to-string
                resultValue: err?.toString(),
                exports: {},
                exportsTest: {},
                exportsGlobal: {},
            },
            success: false,
        };
    } finally {
        thread?.terminate();
    }
}

async function removeFolder(installFolder: string) {
    try {
        await fs.promises.rm(installFolder, { recursive: true, force: true });
    } catch (err) {
        logger.warn('failed to remove install npm packages folder', { err });
    }
}

function getTransactionId(stepResultId: string, testResultId: string, stepId: string, retryIndex: number) {
    return `${testResultId}_${stepId}_${stepResultId}_${retryIndex}`;
}

export async function installPackage(stepId: string, testResultId: string, retryIndex: number, packageData: { packageName: string; packageVersion: string }[], stepResultId: string, timeout: number) {
    const transactionId = getTransactionId(stepResultId, testResultId, stepId, retryIndex);
    const { data } = await runNpmInstall(transactionId, packageData, timeout);
    return data;
}

export async function runCodeWithPackages(
    code: string,
    stepId: string,
    incomingParams: Pick<IncomingParams, 'as' | 'nodePackageParams'>,
    context: { [key: string]: unknown },
    testResultId: string,
    retryIndex: number,
    stepResultId: string,
    timeout: number,
    fileDataUrl?: string,
    s3filepath?: string,
) {
    const computePackagePathFromPackageJsonExports = context.computePackagePathFromPackageJsonExports || featureFlagsService.flags.computePackagePathFromPackageJsonExports.isEnabled();
    const packageLocalLocations = Object.fromEntries(incomingParams.nodePackageParams?.map(data => {
        if (computePackagePathFromPackageJsonExports) {
            const commonJsPath = getLocallyInstalledCommonJsPath(data.testimPackageLocalLocation);
            return [data.paramName, path.join(data.testimPackageLocalLocation, commonJsPath)];
        }

        return [data.paramName, data.testimPackageLocalLocation];
    }) || []);
    const transactionId = getTransactionId(stepResultId, testResultId, stepId, retryIndex);

    if (s3filepath) {
        const s3fileDataUrl = await getS3Artifact(s3filepath);
        if (s3fileDataUrl) {
            fileDataUrl = s3fileDataUrl;
        }
    }

    if (Buffer.isBuffer(fileDataUrl)) { // Fix, S3 is returning a buffer and not a string on empty files
        logger.debug('runCodeWithPackages: converted a buffer to base 64 string data URI', { fileDataUrl });
        fileDataUrl = fileDataUrl.toString();
    }

    const emptyFileDataUrl = 'data:,';
    if (fileDataUrl === 'data:') { // Fix chrome/safari bug that creates malformed data-uri for empty files
        logger.debug('runCodeWithPackages, fileDataUrl was an empty string ', { fileDataUrl });
        fileDataUrl = emptyFileDataUrl;
    }

    const res = await runCodeWithWorkerThread(transactionId, incomingParams, context, code, packageLocalLocations, timeout, fileDataUrl);
    return { ...res, nodeVersion: process.version };
}

async function runNpmInstall(transactionId: string, packageData: { packageName: string; packageVersion: string }[], timeout: number) {
    const packages = packageData.map(data => `${data.packageName}@${data.packageVersion}`);
    const localPackageInstallFolder = getLocalPackageInstallFolder();
    const reuseInstallationFolder = featureFlagsService.flags.reuseCliPkgInstallFolder.isEnabled();
    const installFolder = reuseInstallationFolder ? localPackageInstallFolder : path.join(localPackageInstallFolder, `/${transactionId}`);
    const proxyUri = global.proxyUri;


    async function npmInstallation() {
        let output = '';
        try {
            output = await npmWrapper.installPackages(installFolder, packages, proxyUri, timeout);
            logger.info('npm package install finished', { transactionId, output, timeout });
            if (Number(output.trim().split(' ')[1]) < packages.length) {
                // TODO: I am not sure changing this to an error would be safe.
                // eslint-disable-next-line @typescript-eslint/only-throw-error
                throw 'npm package install failed, couldn\'t install all packages';
            }
            const packageDataWithVersions = packageData.map(pData => {
                const version = npmWrapper.getLocallyInstalledPackageVersion(installFolder, pData.packageName);
                return {
                    ...pData,
                    packageFullName: `${pData.packageName}@${version}`,
                    packageLocalLocation: path.resolve(installFolder, 'node_modules', pData.packageName),
                };
            });

            return { data: packageDataWithVersions, installFolder };
        } catch (err) {
            logger.warn('npm package install failed', { transactionId, err });
            throw err;
        }
    }

    try {
        return await utils.promiseTimeout(npmInstallation(), timeout);
    } catch (err) {
        if (err instanceof TimeoutError) {
            logger.warn('timeout to install package', { packages, transactionId, err, timeout });
        }
        throw err;
    }
}

function getLocalPackageInstallFolder() {
    return path.join(os.tmpdir(), '/testim_local_packages');
}

export function cleanLocalPackageInstallFolder() {
    const localPackageInstallFolder = getLocalPackageInstallFolder();
    return removeFolder(localPackageInstallFolder);
}

// clean local
cleanLocalPackageInstallFolder()
    .catch(err => logger.warn('failed to clean local package folder', { err }));

